import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuthContext } from '../contexts/AuthContext';
import type { MoodBoard, MoodBoardImage, MoodBoardNote } from '../types/moodboard';

export function useMoodBoard() {
  const { user } = useAuthContext();
  const [boards, setBoards] = useState<MoodBoard[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (user) {
      fetchBoards();
    }
  }, [user]);

  const fetchBoards = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('mood_boards')
        .select(`
          *,
          images:mood_board_images(
            id,
            url,
            caption,
            color_palette,
            folder,
            order,
            storage_path,
            created_at
          ),
          notes:mood_board_notes(
            id,
            content,
            color,
            created_at
          )
        `)
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setBoards(data.map(board => ({
        ...board,
        created_at: new Date(board.created_at),
        updated_at: new Date(board.updated_at)
      })));
    } catch (err) {
      console.error('Error fetching mood boards:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch mood boards'));
    } finally {
      setLoading(false);
    }
  };

  const createBoard = async (name: string, description?: string) => {
    try {
      const { data, error } = await supabase
        .from('mood_boards')
        .insert({
          user_id: user?.id,
          name,
          description
        })
        .select()
        .single();

      if (error) throw error;
      
      const newBoard = {
        ...data,
        created_at: new Date(data.created_at),
        updated_at: new Date(data.updated_at),
        images: [],
        notes: []
      };
      
      setBoards(prev => [newBoard, ...prev]);
      return newBoard;
    } catch (err) {
      console.error('Error creating mood board:', err);
      throw err;
    }
  };

  const addImages = async (boardId: string, files: File[]) => {
    try {
      // Get the next order number
      const { data: existingImages } = await supabase
        .from('mood_board_images')
        .select('order')
        .eq('board_id', boardId)
        .order('order', { ascending: false })
        .limit(1);

      let nextOrder = (existingImages?.[0]?.order || 0) + 1;

      // Process each file sequentially
      for await (const file of files) {
        const fileExt = file.name.split('.').pop();
        const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
        const storagePath = `${user?.id}/${boardId}/${fileName}`;

        // Upload file to storage
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('mood-board-images')
          .upload(storagePath, file);

        if (uploadError) throw uploadError;

        // Get public URL with cache buster
        const { data: { publicUrl } } = await supabase.storage
          .from('mood-board-images')
          .getPublicUrl(storagePath);

        const urlWithCacheBuster = `${publicUrl}?t=${Date.now()}`;
        // Create database record
        const { data: imageData, error: dbError } = await supabase
          .from('mood_board_images')
          .insert({
            board_id: boardId,
            url: urlWithCacheBuster,
            storage_path: storagePath,
            caption: file.name,
            order: nextOrder++
          })
          .select()
          .single();

        if (dbError) throw dbError;

        // Update local state immediately after each successful upload
        if (imageData) {
          setBoards(prev => prev.map(board => {
            if (board.id === boardId) {
              return {
                ...board,
                images: [...(board.images || []), imageData]
              };
            }
            return board;
          }));
        }
      }

    } catch (err) {
      console.error('Error adding images:', err);
      throw err;
    }
  };

  const updateImageOrder = async (boardId: string, imageId: string, newOrder: number) => {
    try {
      const { error } = await supabase
        .from('mood_board_images')
        .update({ order: newOrder })
        .eq('id', imageId)
        .eq('board_id', boardId);

      if (error) throw error;
      await fetchBoards();
    } catch (err) {
      console.error('Error updating image order:', err);
      throw err;
    }
  };

  const deleteImage = async (boardId: string, imageId: string) => {
    try {
      // Get image info first
      const { data: image, error: fetchError } = await supabase
        .from('mood_board_images')
        .select('storage_path')
        .eq('id', imageId)
        .single();

      if (fetchError) throw fetchError;

      // Delete from storage if path exists
      if (image?.storage_path) {
        const { error: storageError } = await supabase.storage
          .from('mood-board-images')
          .remove([image.storage_path]);

        if (storageError) throw storageError;
      }

      // Delete database record
      const { error: dbError } = await supabase
        .from('mood_board_images')
        .delete()
        .eq('id', imageId)
        .eq('board_id', boardId);

      if (dbError) throw dbError;
      await fetchBoards();
    } catch (err) {
      console.error('Error deleting image:', err);
      throw err;
    }
  };

  return {
    boards,
    loading,
    error,
    createBoard,
    addImages,
    updateImageOrder,
    deleteImage,
    refresh: fetchBoards
  };
}